import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics'
import { Capacitor } from '@capacitor/core'

/**
 * Composable for triggering haptic feedback on iOS devices
 * Provides different feedback styles for various interaction types
 */
export function useHapticFeedback() {
  const isNative = Capacitor.isNativePlatform()
  const isIOS = Capacitor.getPlatform() === 'ios'

  /**
   * Light impact - for subtle interactions
   * Use for: tab switches, minor UI changes, hover states
   */
  const light = async () => {
    if (!isIOS) return
    try {
      await Haptics.impact({ style: ImpactStyle.Light })
    } catch (error) {
      // Haptics not available
    }
  }

  /**
   * Medium impact - for standard interactions
   * Use for: button presses, navigation, selections
   */
  const medium = async () => {
    if (!isIOS) return
    try {
      await Haptics.impact({ style: ImpactStyle.Medium })
    } catch (error) {
      // Haptics not available
    }
  }

  /**
   * Heavy impact - for significant actions
   * Use for: confirmations, important actions, errors
   */
  const heavy = async () => {
    if (!isIOS) return
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy })
    } catch (error) {
      // Haptics not available
    }
  }

  /**
   * Success notification - for successful operations
   * Use for: form submissions, save actions, completions
   */
  const success = async () => {
    if (!isIOS) return
    try {
      await Haptics.notification({ type: NotificationType.Success })
    } catch (error) {
      // Haptics not available
    }
  }

  /**
   * Warning notification - for warnings
   * Use for: validation warnings, caution states
   */
  const warning = async () => {
    if (!isIOS) return
    try {
      await Haptics.notification({ type: NotificationType.Warning })
    } catch (error) {
      // Haptics not available
    }
  }

  /**
   * Error notification - for errors
   * Use for: validation errors, failed actions
   */
  const error = async () => {
    if (!isIOS) return
    try {
      await Haptics.notification({ type: NotificationType.Error })
    } catch (error) {
      // Haptics not available
    }
  }

  /**
   * Selection changed - for selection interactions
   * Use for: picker changes, toggle switches, checkboxes
   */
  const selection = async () => {
    if (!isIOS) return
    try {
      await Haptics.selectionChanged()
    } catch (error) {
      // Haptics not available
    }
  }

  /**
   * Selection start - call before a series of selections
   */
  const selectionStart = async () => {
    if (!isIOS) return
    try {
      await Haptics.selectionStart()
    } catch (error) {
      // Haptics not available
    }
  }

  /**
   * Selection end - call after a series of selections
   */
  const selectionEnd = async () => {
    if (!isIOS) return
    try {
      await Haptics.selectionEnd()
    } catch (error) {
      // Haptics not available
    }
  }

  return {
    isNative,
    isIOS,
    light,
    medium,
    heavy,
    success,
    warning,
    error,
    selection,
    selectionStart,
    selectionEnd
  }
}
